import { ParamMap } from '@angular/router';

import { IRailingConfig, IRailingStyle, IStairConfig, IStairType, IStringerType, ITurnDirection, ITurnType } from './stair-config.model';

// Stable short URL keys for IStairConfig fields. DO NOT rename or repurpose
// existing entries — share-links saved by users would silently break.
// New fields: append a fresh unique short key here.

type IFieldKind = 'string' | 'number' | 'boolean' | 'nullable-number' | 'enum';

// Декодированное значение одного URL-поля (или undefined, если не распарсилось).
type IDecodedValue = string | number | boolean | null | undefined;

interface IUrlKeyEntry<TPath extends string> {
    readonly path: TPath;
    readonly urlKey: string;
    readonly kind: IFieldKind;
    readonly enumValues?: readonly string[];
}

const STAIR_TYPES: readonly IStairType[] = ['straight', 'l-shape', 'u-shape', 'spiral'];
export const TURN_DIRECTIONS: readonly ITurnDirection[] = ['left', 'right'];
export const TURN_TYPES: readonly ITurnType[] = ['landing', 'winder'];
export const STRINGER_TYPES: readonly IStringerType[] = ['tetiva', 'kosour'];
const RAILING_STYLES: readonly IRailingStyle[] = ['square', 'turned', 'flat', 'glass'];
const RAILING_SIDES: readonly IRailingConfig['sides'][] = ['left', 'right', 'both'];

type IStairConfigFlatKey = Exclude<keyof IStairConfig, 'railing'>;
type IRailingKey = keyof IRailingConfig;

/* eslint-disable sonarjs/no-duplicate-string -- 'kind'-дискриминанты повторяются в стабильной таблице URL-ключей; вынос type-литералов в константы вредит читаемости таблицы. */
const STAIR_CONFIG_KEYS: readonly IUrlKeyEntry<IStairConfigFlatKey>[] = [
    { path: 'type', urlKey: 't', kind: 'enum', enumValues: STAIR_TYPES },
    { path: 'turnDirection', urlKey: 'td', kind: 'enum', enumValues: TURN_DIRECTIONS },
    { path: 'turnType', urlKey: 'tt', kind: 'enum', enumValues: TURN_TYPES },
    { path: 'roomLength', urlKey: 'rl', kind: 'number' },
    { path: 'roomWidth', urlKey: 'rw', kind: 'number' },
    { path: 'floorHeight', urlKey: 'fh', kind: 'number' },
    { path: 'floorThickness', urlKey: 'ft', kind: 'number' },
    { path: 'openingLength', urlKey: 'ol', kind: 'number' },
    { path: 'openingWidth', urlKey: 'ow', kind: 'number' },
    { path: 'openingX', urlKey: 'ox', kind: 'number' },
    { path: 'openingZ', urlKey: 'oz', kind: 'number' },
    { path: 'openingLocked', urlKey: 'olk', kind: 'boolean' },
    { path: 'stairWidth', urlKey: 'sw', kind: 'number' },
    { path: 'stringerType', urlKey: 'st', kind: 'enum', enumValues: STRINGER_TYPES },
    // A link without it opens with risers, as every stair was drawn before the toggle.
    { path: 'risersEnabled', urlKey: 'rse', kind: 'boolean' },
    { path: 'riserHeight', urlKey: 'rh', kind: 'nullable-number' },
    { path: 'treadDepth', urlKey: 'trd', kind: 'nullable-number' },
    { path: 'treadThickness', urlKey: 'trt', kind: 'number' },
    { path: 'noseLength', urlKey: 'nl', kind: 'number' },
    { path: 'stepCount', urlKey: 'sc', kind: 'nullable-number' },
    { path: 'stairAngle', urlKey: 'sa', kind: 'nullable-number' },
    { path: 'treadMaterialId', urlKey: 'tm', kind: 'string' },
    { path: 'riserMaterialId', urlKey: 'rm', kind: 'string' },
    { path: 'stringerMaterialId', urlKey: 'sm', kind: 'string' },
    // A link without these two opens the railing in the treads' species (`railingMaterialsOf`).
    { path: 'handrailMaterialId', urlKey: 'hrm', kind: 'string' },
    { path: 'balusterMaterialId', urlKey: 'blm', kind: 'string' },
    { path: 'turnAtStep', urlKey: 'tas', kind: 'nullable-number' },
    { path: 'flightGap', urlKey: 'fg', kind: 'number' },
    { path: 'spiralDiameter', urlKey: 'sd', kind: 'nullable-number' },
    // No `spiralRotation`: the panel has no control for it, so a link never carries a choice of
    // the user's, only the default of the day it was made. The old `sr=360` links kept a spiral
    // whose exit platform overhung the first steps after the default moved to 330°.
];

const RAILING_KEYS: readonly IUrlKeyEntry<IRailingKey>[] = [
    { path: 'enabled', urlKey: 're', kind: 'boolean' },
    { path: 'style', urlKey: 'rs', kind: 'enum', enumValues: RAILING_STYLES },
    { path: 'height', urlKey: 'rht', kind: 'number' },
    { path: 'balusterWidth', urlKey: 'rbw', kind: 'number' },
    { path: 'balusterSpacing', urlKey: 'rbs', kind: 'number' },
    { path: 'handrailWidth', urlKey: 'rhw', kind: 'number' },
    { path: 'handrailHeight', urlKey: 'rhh', kind: 'number' },
    { path: 'postWidth', urlKey: 'rpw', kind: 'number' },
    { path: 'upperEnabled', urlKey: 'rue', kind: 'boolean' },
    // `rmt` is retired, not free: it carried `railing.materialId`, a field nothing drew, and every
    // old link holds `rmt=oak` whatever the treads were. Such a link is read without it.
    { path: 'sides', urlKey: 'rsd', kind: 'enum', enumValues: RAILING_SIDES },
];
/* eslint-enable sonarjs/no-duplicate-string */

function encodeValue(value: unknown, kind: IFieldKind): string | null {
    if (kind === 'boolean') {
        return value === true ? '1' : '0';
    }
    if (kind === 'nullable-number' && value === null) {
        return 'auto';
    }

    // Остались число, строка и перечисление; всё прочее в адрес не попадает.
    if (typeof value === 'number' || typeof value === 'string') {
        return String(value);
    }

    return null;
    return null;
}

function decodeBoolean(raw: string): boolean | undefined {
    if (raw === '1' || raw === 'true') {
        return true;
    }
    if (raw === '0' || raw === 'false') {
        return false;
    }
    return undefined;
}

function decodeNumeric(raw: string): number | undefined {
    const parsed: number = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
}

// eslint-disable-next-line sonarjs/function-return-type -- полиморфный декодер URL-поля: тип результата зависит от entry.kind по дизайну
function decodeValue(raw: string, entry: IUrlKeyEntry<string>): IDecodedValue {
    switch (entry.kind) {
        case 'boolean':
            return decodeBoolean(raw);
        case 'nullable-number':
            return raw === 'auto' || raw === 'null' ? null : decodeNumeric(raw);
        case 'number':
            return decodeNumeric(raw);
        case 'enum':
            return entry.enumValues?.includes(raw) ? raw : undefined;
        case 'string':
            return raw;
        default:
            return undefined;
    }
}

export function encodeStairConfigToParams(config: IStairConfig): Record<string, string> {
    // Emit every known field so the URL is a complete, self-contained snapshot —
    // identical to what's persisted in storage. This makes shared links stable
    // against future DEFAULT_STAIR_CONFIG changes.
    const out: Record<string, string> = {};

    for (const entry of STAIR_CONFIG_KEYS) {
        const encoded: string | null = encodeValue(config[entry.path], entry.kind);
        if (encoded !== null) {
            out[entry.urlKey] = encoded;
        }
    }

    for (const entry of RAILING_KEYS) {
        const encoded: string | null = encodeValue(config.railing[entry.path], entry.kind);
        if (encoded !== null) {
            out[entry.urlKey] = encoded;
        }
    }

    return out;
}

// Декодирует набор URL-полей в target: пропускает отсутствующие и нераспарсенные.
// Вложенные if вместо continue (sonarjs/too-many-break-or-continue) + дедуп двух проходов.
function decodeEntriesInto(target: Record<string, unknown>, entries: readonly IUrlKeyEntry<string>[], params: ParamMap): void {
    for (const entry of entries) {
        const raw: string | null = params.get(entry.urlKey);
        if (raw !== null) {
            const decoded: IDecodedValue = decodeValue(raw, entry);
            if (decoded !== undefined) {
                target[entry.path] = decoded;
            }
        }
    }
}

export function decodeStairConfigFromParams(params: ParamMap): Partial<IStairConfig> {
    const flat: Partial<IStairConfig> = {};
    const railing: Partial<IRailingConfig> = {};

    decodeEntriesInto(flat, STAIR_CONFIG_KEYS, params);
    decodeEntriesInto(railing, RAILING_KEYS, params);

    if (Object.keys(railing).length > 0) {
        flat.railing = railing as IRailingConfig;
    }

    return flat;
}
